if (!customElements.get('fb-video-carousel')) {
  customElements.define(
    'fb-video-carousel',
    class FbVideoCarousel extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.track = this.querySelector('[data-track]');
        this.tiles = [...this.querySelectorAll('[data-tile]')];
        this.viewer = this.querySelector('[data-viewer]');
        this.viewerVideo = this.querySelector('[data-viewer-video]');
        this.viewerPlaceholder = this.querySelector('[data-viewer-placeholder]');
        this.viewerMute = this.querySelector('[data-viewer-mute]');
        this.shopList = this.querySelector('[data-shop-list]');
        this.shopCtaLabel = this.dataset.shopCtaLabel || 'Shop';
        this.config = this.readConfig();
        this._activeIndex = 0;
        this._viewerOpenedAt = 0;
        this._analytics = window.FbVideoAnalytics;
        this._viewerPortaled = false;
        this._portalMq = window.matchMedia('(max-width: 989px)');
        this._tileVisibility = new Map();
        this._impressedTiles = new Set();

        if (!this.track || this.tiles.length === 0) return;

        this.querySelector('[data-prev]')?.addEventListener('click', () => this.scrollByTiles(-1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.scrollByTiles(1));
        this.track.addEventListener('scroll', () => {
          this.updateArrows();
          this.syncTilePlayback();
        }, { passive: true });

        this.tiles.forEach((tile, index) => {
          const video = tile.querySelector('video');
          const playBtn = tile.querySelector('[data-open-viewer]');
          const expandBtn = tile.querySelector('[data-expand]');
          const muteBtn = tile.querySelector('[data-tile-mute]');
          const media = tile.querySelector('[data-media]');

          const openViewer = (e) => {
            if (e?.target?.closest('[data-tile-mute]')) return;
            this.openViewer(index);
          };

          playBtn?.addEventListener('click', openViewer);
          expandBtn?.addEventListener('click', openViewer);
          if (this.config.openViewerOnTileClick) {
            media?.addEventListener('click', openViewer);
          }

          muteBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!video) return;
            video.muted = !video.muted;
            muteBtn.classList.toggle('is-muted', video.muted);
            muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
            this.trackEvent(this.metaFromTile(tile), 'mute_toggle');
          });

          tile.querySelector('video')?.addEventListener('click', (e) => e.stopPropagation());

          video?.addEventListener('play', () => {
            this._analytics?.tilePlay(this.metaFromTile(tile));
          });

          video?.addEventListener('ended', () => {
            if (this.config.tileLoop) return;
            if (this.viewer?.classList.contains('is-open') && this.config.pauseTilesInViewer) return;
            if (this.config.tilePlayback !== 'one_at_a_time') return;
            this.advanceCarouselAfterTileEnd(tile);
          });
        });

        this.bindViewerEvents();

        document.addEventListener(
          'keydown',
          (this._onKeydown = (e) => {
            if (!this.viewer?.classList.contains('is-open')) return;
            if (e.key === 'Escape') this.closeViewer();
            if (e.key === 'ArrowLeft') this.stepViewer(-1);
            if (e.key === 'ArrowRight') this.stepViewer(1);
          })
        );

        this.setupTilePlaybackObserver();
        this.syncTilePlayback();
        this.updateArrows();
      }

      readConfig() {
        const threshold = parseFloat(this.dataset.tileThreshold || '0.55');
        return {
          tilePlayback: this.dataset.tilePlayback || 'one_at_a_time',
          tileThreshold: Number.isFinite(threshold) ? Math.min(1, Math.max(0, threshold)) : 0.55,
          tileMuted: this.dataset.tileMuted !== 'false',
          tileLoop: this.dataset.tileLoop !== 'false',
          viewerAutoplay: this.dataset.viewerAutoplay !== 'false',
          viewerMuted: this.dataset.viewerMuted === 'true',
          viewerLoop: this.dataset.viewerLoop !== 'false',
          viewerClickToggle: this.dataset.viewerClickToggle === 'true',
          pauseTilesInViewer: this.dataset.pauseTilesInViewer !== 'false',
          openViewerOnTileClick: this.dataset.openViewerOnClick !== 'false',
        };
      }

      setupTilePlaybackObserver() {
        this._io?.disconnect();
        if (this.config.tilePlayback === 'none') return;

        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._tileVisibility.set(entry.target, entry.intersectionRatio);
            });
            this.syncTilePlayback();
          },
          { root: this.track, threshold: [0, 0.1, 0.25, 0.4, 0.55, 0.7, 0.85, 1] }
        );
        this.tiles.forEach((tile) => this._io.observe(tile));
      }

      syncTilePlayback() {
        if (this.config.tilePlayback === 'none') return;
        if (this.config.pauseTilesInViewer && this.viewer?.classList.contains('is-open')) {
          this.pauseAllTileVideos();
          return;
        }

        const min = this.config.tileThreshold;
        const mode = this.config.tilePlayback;

        if (mode === 'one_at_a_time') {
          let bestTile = null;
          let bestRatio = min;
          this.tiles.forEach((tile) => {
            const ratio = this._tileVisibility.get(tile) || 0;
            if (ratio >= min && ratio > bestRatio) {
              bestRatio = ratio;
              bestTile = tile;
            }
          });
          this.tiles.forEach((tile) => {
            const video = tile.querySelector('video');
            if (!video) return;
            if (tile === bestTile) this.playTileVideo(tile, video);
            else video.pause();
          });
          return;
        }

        this.tiles.forEach((tile) => {
          const video = tile.querySelector('video');
          if (!video) return;
          const ratio = this._tileVisibility.get(tile) || 0;
          if (ratio >= min) this.playTileVideo(tile, video);
          else video.pause();
        });
      }

      playTileVideo(tile, video) {
        if (video.paused) {
          video.play().catch(() => {});
          if (!this._impressedTiles.has(tile)) {
            this._impressedTiles.add(tile);
            this._analytics?.impression(this.metaFromTile(tile));
          }
        }
      }

      pauseAllTileVideos() {
        this.tiles.forEach((tile) => tile.querySelector('video')?.pause());
      }

      findNextPlayableTileIndex(fromIndex) {
        const len = this.tiles.length;
        if (len < 2) return -1;
        for (let step = 1; step <= len; step += 1) {
          const idx = (fromIndex + step) % len;
          const tile = this.tiles[idx];
          if (tile.dataset.placeholder === 'true') continue;
          if (tile.querySelector('video')) return idx;
        }
        return -1;
      }

      advanceCarouselAfterTileEnd(tile) {
        const fromIndex = this.tiles.indexOf(tile);
        if (fromIndex < 0) return;

        const currentVideo = tile.querySelector('video');
        if (currentVideo) {
          currentVideo.pause();
          currentVideo.currentTime = 0;
        }

        const nextIndex = this.findNextPlayableTileIndex(fromIndex);
        if (nextIndex < 0) return;

        const nextTile = this.tiles[nextIndex];
        const nextVideo = nextTile.querySelector('video');
        if (!nextVideo) return;

        this.tiles.forEach((t) => {
          const v = t.querySelector('video');
          if (!v || t === nextTile) return;
          v.pause();
          if (t === tile) v.currentTime = 0;
        });

        nextVideo.currentTime = 0;
        nextTile.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        this.playTileVideo(nextTile, nextVideo);
        this.updateArrows();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeydown);
        this._io?.disconnect();
        this.teardownShopCarousel();
        this.unportalViewer();
      }

      bindViewerEvents() {
        if (!this.viewer || this._viewerEventsBound) return;
        this._viewerEventsBound = true;

        this.viewer.addEventListener('click', (e) => {
          if (e.target.closest('[data-viewer-close]') || e.target.closest('[data-viewer-backdrop]')) {
            this.closeViewer();
            return;
          }
          if (e.target.closest('[data-viewer-prev]')) {
            this.trackEvent(this.metaFromTile(this.tiles[this._activeIndex]), 'viewer_prev');
            this.stepViewer(-1);
            return;
          }
          if (e.target.closest('[data-viewer-next]')) {
            this.trackEvent(this.metaFromTile(this.tiles[this._activeIndex]), 'viewer_next');
            this.stepViewer(1);
            return;
          }
          if (e.target.closest('[data-viewer-mute]')) {
            this.toggleViewerMute();
            this.trackEvent(this.metaFromTile(this.tiles[this._activeIndex]), 'viewer_mute');
            return;
          }
          if (
            this.config.viewerClickToggle &&
            e.target.closest('[data-viewer-video]') &&
            this.viewerVideo &&
            !this.viewerVideo.hidden
          ) {
            if (this.viewerVideo.paused) this.viewerVideo.play().catch(() => {});
            else this.viewerVideo.pause();
            return;
          }
          const link = e.target.closest('[data-shop-link]');
          if (link) {
            const tile = this.tiles[this._activeIndex];
            const product = this.getProductByVariantId(tile, link.dataset.variantId) || {
              productId: link.dataset.productId,
              title: link.dataset.productTitle,
              url: link.href,
            };
            this._analytics?.productClick(this.metaFromTile(tile), product);
          }
        });
      }

      syncViewerRefs() {
        if (!this.viewer) return;
        this.viewerVideo = this.viewer.querySelector('[data-viewer-video]');
        this.viewerPlaceholder = this.viewer.querySelector('[data-viewer-placeholder]');
        this.viewerMute = this.viewer.querySelector('[data-viewer-mute]');
        this.shopList = this.viewer.querySelector('[data-shop-list]');
      }

      shouldPortalViewer() {
        return this._portalMq?.matches ?? window.innerWidth <= 989;
      }

      portalViewer() {
        if (!this.viewer || this._viewerPortaled) return;
        const rootStyle = getComputedStyle(this);
        ['--fb-vc-viewer-width', '--fb-vc-radius', '--fb-vc-ratio'].forEach((prop) => {
          const val = rootStyle.getPropertyValue(prop).trim();
          if (val) this.viewer.style.setProperty(prop, val);
        });
        document.body.appendChild(this.viewer);
        this.viewer.classList.add('is-portaled');
        this._viewerPortaled = true;
        this.syncViewerRefs();
      }

      unportalViewer() {
        if (!this.viewer || !this._viewerPortaled) return;
        this.appendChild(this.viewer);
        this.viewer.classList.remove('is-portaled');
        this._viewerPortaled = false;
        this.syncViewerRefs();
      }

      analyticsEnabled() {
        return this.dataset.analyticsEnabled !== 'false' && !!this._analytics;
      }

      metaFromTile(tile) {
        return {
          enabled: this.analyticsEnabled(),
          sectionId: tile?.dataset.sectionId || this.dataset.sectionId || '',
          blockId: tile?.dataset.blockId || '',
          videoTitle: tile?.dataset.videoTitle || '',
          videoId: tile?.dataset.videoId || '',
          pageType: this.dataset.pageType || '',
        };
      }

      trackEvent(meta, interactionType) {
        this._analytics?.interaction(meta, interactionType);
      }

      getTileProducts(tile) {
        const node = tile.querySelector('[data-shop-products]');
        if (!node) return [];
        try {
          return this.dedupeShopProducts(JSON.parse(node.textContent) || []);
        } catch {
          return [];
        }
      }

      dedupeShopProducts(products) {
        const seen = new Set();
        return products.filter((product) => {
          const key = String(product.productId || product.variantId || '');
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      getProductByVariantId(tile, variantId) {
        return this.getTileProducts(tile).find((p) => String(p.variantId) === String(variantId));
      }

      scrollByTiles(direction) {
        const tile = this.tiles[0];
        if (!tile) return;
        const step = tile.offsetWidth + parseFloat(getComputedStyle(this.track).gap || 7);
        this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      updateArrows() {
        const prev = this.querySelector('[data-prev]');
        const next = this.querySelector('[data-next]');
        const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
        if (prev) prev.disabled = this.track.scrollLeft <= 2;
        if (next) next.disabled = this.track.scrollLeft >= maxScroll;
      }

      openViewer(index) {
        this._activeIndex = index;
        this._viewerOpenedAt = Date.now();
        const meta = this.metaFromTile(this.tiles[index]);
        this._analytics?.viewerOpen(meta);
        if (this.shouldPortalViewer()) this.portalViewer();
        this.syncViewerRefs();
        this.viewer?.classList.add('is-open');
        if (this.config.pauseTilesInViewer) this.pauseAllTileVideos();
        this.renderViewerSlide();
      }

      closeViewer() {
        const tile = this.tiles[this._activeIndex];
        if (tile && this._viewerOpenedAt) {
          this._analytics?.viewerClose(this.metaFromTile(tile), Date.now() - this._viewerOpenedAt);
        }
        this._viewerOpenedAt = 0;
        this.viewer?.classList.remove('is-open');
        this.unportalViewer();
        if (this.viewerVideo) {
          this.viewerVideo.pause();
          this.viewerVideo.removeAttribute('src');
          this.viewerVideo.innerHTML = '';
          this.viewerVideo.hidden = true;
        }
        this.viewerPlaceholder?.setAttribute('hidden', '');
        this.viewerMute?.setAttribute('hidden', '');
        this.syncTilePlayback();
      }

      stepViewer(delta) {
        const next = this._activeIndex + delta;
        if (next < 0 || next >= this.tiles.length) return;
        this._activeIndex = next;
        this._viewerOpenedAt = Date.now();
        this._analytics?.viewerOpen(this.metaFromTile(this.tiles[next]));
        this.renderViewerSlide();
      }

      renderViewerSlide() {
        const tile = this.tiles[this._activeIndex];
        if (!tile) return;

        const isPlaceholder = tile.dataset.placeholder === 'true';
        const prevBtn = this.viewer?.querySelector('[data-viewer-prev]');
        const nextBtn = this.viewer?.querySelector('[data-viewer-next]');
        if (prevBtn) prevBtn.disabled = this._activeIndex <= 0;
        if (nextBtn) nextBtn.disabled = this._activeIndex >= this.tiles.length - 1;

        if (isPlaceholder) {
          this.viewerVideo?.pause();
          if (this.viewerVideo) {
            this.viewerVideo.hidden = true;
            this.viewerVideo.innerHTML = '';
          }
          this.viewerPlaceholder?.removeAttribute('hidden');
          this.viewerMute?.setAttribute('hidden', '');
        } else if (this.viewerVideo) {
          this.viewerPlaceholder?.setAttribute('hidden', '');
          this.viewerMute?.removeAttribute('hidden');
          this.viewerVideo.removeAttribute('hidden');

          const srcTileVideo = tile.querySelector('video');
          const sources = srcTileVideo ? [...srcTileVideo.querySelectorAll('source')] : [];
          const poster = srcTileVideo?.getAttribute('poster') || '';

          this.viewerVideo.pause();
          this.viewerVideo.innerHTML = '';
          if (sources.length) {
            sources.forEach((s) => {
              const clone = document.createElement('source');
              clone.src = s.src;
              clone.type = s.type;
              this.viewerVideo.appendChild(clone);
            });
          } else if (srcTileVideo?.src) {
            this.viewerVideo.src = srcTileVideo.src;
          }
          if (poster) this.viewerVideo.poster = poster;
          this.viewerVideo.muted = this.config.viewerMuted;
          this.viewerVideo.loop = this.config.viewerLoop;
          this.viewerVideo.playsInline = true;
          this.viewerVideo.load();
          if (this.config.viewerAutoplay) {
            this.viewerVideo.play().catch(() => {});
          } else {
            this.viewerVideo.pause();
          }

          this.viewerMute?.classList.toggle('is-muted', this.viewerVideo.muted);
          this.viewerMute?.setAttribute(
            'aria-label',
            this.viewerVideo.muted ? 'Unmute video' : 'Mute video'
          );
        }

        this.renderShopList(tile);
      }

      teardownShopCarousel() {
        this._shopCarouselCleanup?.();
        this._shopCarouselCleanup = null;
      }

      renderShopCard(p) {
        return `
          <div class="fb-video-carousel__shop">
            <img class="fb-video-carousel__shop-image" src="${p.image}" alt="${this.escapeHtml(p.title)}" width="56" height="56" loading="lazy">
            <div class="fb-video-carousel__shop-info">
              <p class="fb-video-carousel__shop-title">${this.escapeHtml(p.title)}</p>
              <p class="fb-video-carousel__shop-price">${this.escapeHtml(p.price)}</p>
            </div>
            <div class="fb-video-carousel__shop-actions">
              <a class="fb-video-carousel__shop-btn fb-video-carousel__shop-btn--shop" data-shop-link href="${p.url}" data-variant-id="${p.variantId}" data-product-id="${p.productId}" data-product-title="${this.escapeHtml(p.title)}">${this.escapeHtml(this.shopCtaLabel)}</a>
            </div>
          </div>`;
      }

      renderShopList(tile) {
        if (!this.shopList) return;
        this.teardownShopCarousel();
        const products = this.getTileProducts(tile);

        if (!products.length) {
          this.shopList.hidden = true;
          this.shopList.classList.remove('fb-video-carousel__shop-list--carousel');
          this.shopList.innerHTML = '';
          return;
        }

        const cards = products.map((p) => this.renderShopCard(p)).join('');
        const isCarousel = products.length > 1;

        this.shopList.hidden = false;
        this.shopList.classList.toggle('fb-video-carousel__shop-list--carousel', isCarousel);

        if (isCarousel) {
          const dots = products
            .map(
              (_, i) =>
                `<button type="button" class="fb-video-carousel__shop-dot${i === 0 ? ' is-active' : ''}" data-shop-dot="${i}" aria-label="Product ${i + 1} of ${products.length}"></button>`
            )
            .join('');
          this.shopList.innerHTML = `
            <div class="fb-video-carousel__shop-carousel">
              <div class="fb-video-carousel__shop-dots" data-shop-dots role="tablist" aria-label="Products">${dots}</div>
              <div class="fb-video-carousel__shop-track" data-shop-track>${cards}</div>
            </div>`;
          this.bindShopCarousel();
        } else {
          this.shopList.innerHTML = cards;
        }
      }

      shopCarouselIsHorizontal() {
        return this._shopCarouselMq?.matches ?? window.innerWidth >= 990;
      }

      bindShopCarousel() {
        const track = this.shopList?.querySelector('[data-shop-track]');
        const dotsContainer = this.shopList?.querySelector('[data-shop-dots]');
        const dots = [...(this.shopList?.querySelectorAll('[data-shop-dot]') || [])];
        if (!track || !dots.length) return;

        this._shopCarouselMq = this._shopCarouselMq || window.matchMedia('(min-width: 990px)');

        const scrollSlideIntoView = (slide) => {
          if (!slide) return;
          const horizontal = this.shopCarouselIsHorizontal();
          slide.scrollIntoView({
            behavior: 'smooth',
            block: horizontal ? 'nearest' : 'start',
            inline: horizontal ? 'start' : 'nearest',
          });
        };

        const updateDots = () => {
          const slide = track.querySelector('.fb-video-carousel__shop');
          const horizontal = this.shopCarouselIsHorizontal();
          let index = 0;
          if (horizontal) {
            const slideW = slide?.offsetWidth || 0;
            index = slideW > 0 ? Math.round(track.scrollLeft / slideW) : 0;
          } else {
            const slideH = slide?.offsetHeight || 0;
            index = slideH > 0 ? Math.round(track.scrollTop / slideH) : 0;
          }
          const clamped = Math.min(Math.max(index, 0), dots.length - 1);
          if (dotsContainer) {
            dotsContainer.setAttribute('aria-orientation', horizontal ? 'horizontal' : 'vertical');
          }
          dots.forEach((dot, i) => {
            dot.classList.toggle('is-active', i === clamped);
            dot.setAttribute('aria-selected', i === clamped ? 'true' : 'false');
          });
        };

        const onScroll = () => {
          if (this._shopScrollRaf) cancelAnimationFrame(this._shopScrollRaf);
          this._shopScrollRaf = requestAnimationFrame(updateDots);
        };

        const onWheel = (e) => {
          if (!this.shopCarouselIsHorizontal()) return;
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
          e.preventDefault();
          track.scrollLeft += e.deltaY;
        };

        const onMqChange = () => {
          updateDots();
        };

        track.addEventListener('scroll', onScroll, { passive: true });
        track.addEventListener('wheel', onWheel, { passive: false });
        this._shopCarouselMq.addEventListener('change', onMqChange);

        dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            scrollSlideIntoView(track.children[Number(dot.dataset.shopDot)]);
          });
        });

        updateDots();

        this._shopCarouselCleanup = () => {
          track.removeEventListener('scroll', onScroll);
          track.removeEventListener('wheel', onWheel);
          this._shopCarouselMq?.removeEventListener('change', onMqChange);
          if (this._shopScrollRaf) cancelAnimationFrame(this._shopScrollRaf);
        };
      }

      escapeHtml(str) {
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      toggleViewerMute() {
        if (!this.viewerVideo || !this.viewerMute) return;
        this.viewerVideo.muted = !this.viewerVideo.muted;
        this.viewerMute.classList.toggle('is-muted', this.viewerVideo.muted);
        this.viewerMute.setAttribute('aria-label', this.viewerVideo.muted ? 'Unmute video' : 'Mute video');
      }

    }
  );
}
